/* Room filmstrip
 * Cycles a product's photos in place on the style pages.
 *
 * Design notes:
 * - Arrow keys act only when focus is inside a filmstrip (or its group
 *   control), so they never fight with page scrolling and are never
 *   ambiguous when several filmstrips are on one page.
 * - Inactive slides are display:none, so their loading="lazy" images are not
 *   fetched on page load. Neighbours are fetched on first contact with a
 *   strip, which makes the first click instant without loading everything.
 * - With one photo the markup is already a plain static image; this script
 *   simply finds no controls to wire up.
 */
(function () {
  'use strict';

  function slidesOf(film) {
    return film.querySelectorAll('[data-film-slide]');
  }

  function indexOf(film) {
    var i = parseInt(film.getAttribute('data-film-index'), 10);
    return isNaN(i) ? 0 : i;
  }

  function hydrate(slide) {
    if (!slide) return;
    var img = slide.querySelector('img');
    if (img && img.getAttribute('loading') === 'lazy') {
      img.setAttribute('loading', 'eager');
    }
  }

  function preloadNeighbours(film) {
    var s = slidesOf(film);
    var n = s.length;
    if (n < 2) return;
    var i = indexOf(film);
    hydrate(s[(i + 1) % n]);
    hydrate(s[(i - 1 + n) % n]);
  }

  function show(film, idx) {
    var s = slidesOf(film);
    var n = s.length;
    if (n < 2) return;
    idx = ((idx % n) + n) % n;
    film.setAttribute('data-film-index', String(idx));

    for (var k = 0; k < n; k++) {
      var active = k === idx;
      s[k].classList.toggle('is-active', active);
      if (active) {
        s[k].removeAttribute('aria-hidden');
        s[k].removeAttribute('tabindex');
      } else {
        s[k].setAttribute('aria-hidden', 'true');
        s[k].setAttribute('tabindex', '-1');
      }
    }

    var dots = film.querySelectorAll('[data-film-dot]');
    for (var d = 0; d < dots.length; d++) {
      dots[d].classList.toggle('is-active', d === idx);
    }

    var cap = film.querySelector('[data-film-caption]');
    if (cap) cap.textContent = s[idx].getAttribute('data-room') || '';

    preloadNeighbours(film);
  }

  function step(film, dir) {
    show(film, indexOf(film) + dir);
  }

  function groupFilms(gid) {
    return document.querySelectorAll('[data-film-group="' + gid.replace(/"/g, '') + '"]');
  }

  function groupStep(gid, dir) {
    var films = groupFilms(gid);
    for (var i = 0; i < films.length; i++) step(films[i], dir);
  }

  /* ---- clicks ---- */
  document.addEventListener('click', function (e) {
    var t = e.target;
    if (!t || !t.closest) return;

    var dot = t.closest('[data-film-dot]');
    if (dot) {
      e.preventDefault();
      show(dot.closest('[data-film]'), parseInt(dot.getAttribute('data-film-dot'), 10) || 0);
      return;
    }

    var prev = t.closest('[data-film-prev]');
    if (prev) { e.preventDefault(); step(prev.closest('[data-film]'), -1); return; }

    var next = t.closest('[data-film-next]');
    if (next) { e.preventDefault(); step(next.closest('[data-film]'), 1); return; }

    var gPrev = t.closest('[data-film-group-prev]');
    if (gPrev) {
      e.preventDefault();
      groupStep(gPrev.closest('[data-film-group-ctrl]').getAttribute('data-film-group-ctrl'), -1);
      return;
    }

    var gNext = t.closest('[data-film-group-next]');
    if (gNext) {
      e.preventDefault();
      groupStep(gNext.closest('[data-film-group-ctrl]').getAttribute('data-film-group-ctrl'), 1);
    }
  });

  /* ---- keyboard: only while focus is inside a strip or its control ---- */
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    if (e.altKey || e.ctrlKey || e.metaKey) return;

    var t = e.target;
    if (!t || !t.closest) return;
    var dir = e.key === 'ArrowRight' ? 1 : -1;

    var ctrl = t.closest('[data-film-group-ctrl]');
    if (ctrl) {
      e.preventDefault();
      groupStep(ctrl.getAttribute('data-film-group-ctrl'), dir);
      return;
    }

    var film = t.closest('[data-film]');
    if (!film) return;
    e.preventDefault();
    var gid = film.getAttribute('data-film-group');
    if (gid) groupStep(gid, dir); else step(film, dir);
  });

  /* ---- swipe ---- */
  function wireSwipe(film) {
    var x0 = null, y0 = null;
    film.addEventListener('touchstart', function (e) {
      if (e.touches.length !== 1) { x0 = null; return; }
      x0 = e.touches[0].clientX;
      y0 = e.touches[0].clientY;
    }, { passive: true });

    film.addEventListener('touchend', function (e) {
      if (x0 === null) return;
      var t = e.changedTouches[0];
      var dx = t.clientX - x0;
      var dy = t.clientY - y0;
      x0 = null;
      if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy)) return;
      var dir = dx < 0 ? 1 : -1;
      var gid = film.getAttribute('data-film-group');
      if (gid) groupStep(gid, dir); else step(film, dir);
    }, { passive: true });
  }

  /* ---- init ---- */
  function init(root) {
    var films = (root || document).querySelectorAll('[data-film]');
    for (var i = 0; i < films.length; i++) {
      var film = films[i];
      if (film.__filmReady) continue;
      film.__filmReady = true;

      var s = slidesOf(film);
      if (s.length < 2) continue;

      var cap = film.querySelector('[data-film-caption]');
      if (cap) cap.textContent = s[indexOf(film)].getAttribute('data-room') || '';

      wireSwipe(film);

      /* Fetch the neighbouring photos on first contact with this strip, not on
         scroll. A lookbook page carries dozens of strips; preloading them all
         as they scroll past would triple the page's image weight for visitors
         who never touch one. Hovering, tabbing to or touching a strip is the
         signal that its next photo is about to be wanted. */
      (function (f) {
        function warm() {
          preloadNeighbours(f);
          f.removeEventListener('pointerenter', warm);
          f.removeEventListener('focusin', warm);
          f.removeEventListener('touchstart', warm);
        }
        f.addEventListener('pointerenter', warm);
        f.addEventListener('focusin', warm);
        f.addEventListener('touchstart', warm, { passive: true });
      })(film);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  /* Theme editor re-renders sections without a page reload */
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();